import { useState, useEffect, useCallback, useMemo } from 'react';
import authHeader from 'services/auth/jwt/auth-header';
import customAxios from '../util/Api';
import useGetUser from 'routes/Auth/useGetUser';
import { toLocalISO } from '../GenTask/components/SharedFormFields';

const TASKS_ENDPOINT = '/TaskManagement/tasks';
const STATUSES_ENDPOINT = '/TaskManagement/statuses';
const LOGIN_TOKEN_ENDPOINT = '/TaskManagement/AddLoginToken';
const HELPDESK_LOGIN_URL = 'https://app.focus247.online/login-with-token';

export const PRIORITIES = [
    { id: 1, name: 'Low', color: '#7C9473' },
    { id: 2, name: 'Normal', color: '#C6A15B' },
    { id: 3, name: 'Medium', color: '#C98A2C' },
    { id: 4, name: 'High', color: '#C1622D' },
    { id: 5, name: 'Urgent', color: '#A83E32' },
];

export function getPriority(priorityId) {
    return PRIORITIES.find((p) => p.id === priorityId) || PRIORITIES[1];
}

/** Strips HTML tags for a plain-text preview of the rich-text description. */
export function stripHtml(html = '') {
    return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

export function formatDate(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// 10-digit numeric token using the browser's CSPRNG
const generateToken = (length = 10) => {
    const values = new Uint32Array(length);
    window.crypto.getRandomValues(values);
    return Array.from(values, (n) => n % 10).join('');
};

export const LIST_COLUMNS = [
    {
        id: 'ref',
        label: 'Ticket #',
        text: (t) => String(t.tRefNumber || t.tId),
        sort: (t) => Number(t.tRefNumber || t.tId) || 0,
    },
    {
        id: 'taskName',
        label: 'Title',
        text: (t) => t.taskName || '',
        sort: (t) => (t.taskName || '').toLowerCase(),
    },
    {
        id: 'priority',
        label: 'Priority',
        type: 'select',
        text: (t) => getPriority(t.priorityId).name,
        sort: (t) => Number(t.priorityId) || 0,
    },
    {
        id: 'status',
        label: 'Status',
        type: 'select',
        text: (t) => t.statusDescription || '',
        sort: (t) => (t.statusDescription || '').toLowerCase(),
    },
    {
        id: 'due',
        label: 'Due',
        text: (t) => formatDate(t.tDueDate),
        sort: (t) => (t.tDueDate ? new Date(t.tDueDate).getTime() : 0),
    },
    {
        id: 'reportedBy',
        label: 'Reported by',
        text: (t) => t.reportedByName || 'Unknown',
        sort: (t) => (t.reportedByName || '').toLowerCase(),
    },
    {
        id: 'created',
        label: 'Created',
        text: (t) => formatDate(t.dateCreated),
        sort: (t) => (t.dateCreated ? new Date(t.dateCreated).getTime() : 0),
    },
];

const EMPTY_FILTERS = LIST_COLUMNS.reduce((acc, c) => ({ ...acc, [c.id]: [] }), {});

export default function useHelpDesk({ userId, projectId, apiBaseUrl = '', getAuthHeaders } = {}) {
    const { user } = useGetUser();

    const userEmail = user?.email || user?.username || user?.emailAddress || '';
    const reportedByName = user?.defaultDriverDisplayName || '';

    // ---- Data ----
    const [tasks, setTasks] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // ---- View / filter state ----
    const [activeStatusId, setActiveStatusId] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState('list');
    const [sortState, setSortState] = useState({ by: 'created', dir: 'desc' });
    const [columnFilters, setColumnFilters] = useState(EMPTY_FILTERS);

    // ---- Modals ----
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedGridTask, setSelectedGridTask] = useState(null);

    // ---- Column filter popover ----
    const [filterPopover, setFilterPopover] = useState({ anchorEl: null, colId: null });
    const [draftSelection, setDraftSelection] = useState([]);
    const [optionSearch, setOptionSearch] = useState('');

    // ---- External link ----
    const [isOpeningTicket, setIsOpeningTicket] = useState(false);
    const [linkError, setLinkError] = useState('');

    const tasksUrl = `${apiBaseUrl}${TASKS_ENDPOINT}`;
    const statusesUrl = `${apiBaseUrl}${STATUSES_ENDPOINT}`;

    const isSearching = searchQuery.trim().length > 0;

    // ---------- Loading ----------
    const loadAll = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [taskRes, statusRes] = await Promise.all([
                customAxios.get(tasksUrl, { headers: authHeader() }),
                customAxios.get(statusesUrl, { headers: authHeader() }),
            ]);
            const taskData = taskRes.data;
            const statusData = statusRes.data;
            setTasks(Array.isArray(taskData) ? taskData : []);
            setStatuses(
                Array.isArray(statusData) ? [...statusData].sort((a, b) => a.sortOrder - b.sortOrder) : []
            );
        } catch (err) {
            setError(err.message || 'Something went wrong loading the help desk.');
        } finally {
            setIsLoading(false);
        }
    }, [tasksUrl, statusesUrl, getAuthHeaders]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    // ---------- Create ----------
    const createTask = useCallback(
        async ({ taskName, taskDescription, statusId, priorityId, taskDueDate }) => {
            setIsSubmitting(true);
            setError(null);
            try {
                const payload = {
                    userId,
                    projectId,
                    reportedByEmail: userEmail,
                    reportedByName,
                    reportedByClient: 'Logwatch',
                    taskName,
                    statusId,
                    taskDescription,
                    taskDueDate: taskDueDate || toLocalISO(),
                    priorityId,
                };
                await customAxios.post(tasksUrl, payload, { headers: authHeader() });
                await loadAll();
                return true;
            } catch (err) {
                setError(err.message || 'Could not create the task.');
                return false;
            } finally {
                setIsSubmitting(false);
            }
        },
        [tasksUrl, userId, projectId, userEmail, reportedByName, loadAll]
    );

    const openCreateModal = () => setIsModalOpen(true);
    const closeCreateModal = () => setIsModalOpen(false);

    const handleSubmitTask = async (payload) => {
        const success = await createTask(payload);
        if (success) setIsModalOpen(false);
    };

    // ---------- Search + tab filtering ----------
    const countsByStatus = useMemo(() => {
        const counts = {};
        tasks.forEach((t) => {
            counts[t.statusId] = (counts[t.statusId] || 0) + 1;
        });
        return counts;
    }, [tasks]);

    // Global search: matches name, description, ref number and status name.
    // Ignores the active tab so results aren't hidden.
    const searchedTasks = useMemo(() => {
        if (!isSearching) return tasks;
        const q = searchQuery.trim().toLowerCase();

        const statusNameById = statuses.reduce((acc, s) => {
            acc[s.id] = (s.statusName || '').toLowerCase();
            return acc;
        }, {});

        return tasks.filter((t) => {
            const name = (t.taskName || '').toLowerCase();
            const desc = stripHtml(t.taskDescription || '').toLowerCase();
            const ref = String(t.tRefNumber || '').toLowerCase();
            const status = statusNameById[t.statusId] || '';
            return name.includes(q) || desc.includes(q) || ref.includes(q) || status.includes(q);
        });
    }, [tasks, searchQuery, isSearching, statuses]);

    // What the UI shows: search results, or the active tab's tasks, newest first.
    const displayedTasks = useMemo(() => {
        const filtered = isSearching
            ? searchedTasks
            : activeStatusId === 'all'
                ? tasks
                : tasks.filter((t) => t.statusId === activeStatusId);
        return [...filtered].sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));
    }, [tasks, searchedTasks, activeStatusId, isSearching]);

    // Tab badges: counts across all tickets, or across search results while searching.
    const displayCounts = useMemo(() => {
        if (!isSearching) return countsByStatus;
        const counts = {};
        searchedTasks.forEach((t) => {
            counts[t.statusId] = (counts[t.statusId] || 0) + 1;
        });
        return counts;
    }, [isSearching, countsByStatus, searchedTasks]);

    const totalBadgeCount = isSearching ? searchedTasks.length : tasks.length;

    // Auto-switch to "All" when a search starts so the tab indicator matches the results.
    useEffect(() => {
        if (isSearching && activeStatusId !== 'all') {
            setActiveStatusId('all');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSearching]);

    const activeTabIndex = useMemo(() => {
        if (activeStatusId === 'all') return 0;
        const idx = statuses.findIndex((s) => s.id === activeStatusId);
        return idx === -1 ? 0 : idx + 1;
    }, [activeStatusId, statuses]);

    const handleTabChange = (_, newIndex) => {
        setActiveStatusId(newIndex === 0 ? 'all' : statuses[newIndex - 1].id);
    };

    // ---------- Grid view ----------
    const gridGroups = useMemo(() => {
        const knownStatusIds = new Set(statuses.map((s) => String(s.id)));
        const groups = statuses.map((status) => ({
            id: status.id,
            name: status.statusName,
            tasks: displayedTasks.filter((t) => String(t.statusId) === String(status.id)),
        }));
        const uncategorized = displayedTasks.filter((t) => !knownStatusIds.has(String(t.statusId)));
        if (uncategorized.length > 0) {
            groups.push({ id: 'other', name: 'Other', tasks: uncategorized });
        }
        return activeStatusId === 'all' ? groups : groups.filter((g) => g.tasks.length > 0);
    }, [statuses, displayedTasks, activeStatusId]);

    // ---------- List view: sorting + column filters ----------
    const hasColumnFilters = Object.values(columnFilters).some((v) => v.length > 0);

    const handleSort = (columnId) => {
        setSortState((prev) =>
            prev.by === columnId
                ? { by: columnId, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                : { by: columnId, dir: 'asc' }
        );
    };

    const handleColumnFilter = (columnId, value) =>
        setColumnFilters((prev) => ({ ...prev, [columnId]: value }));

    const clearColumnFilters = () => setColumnFilters(EMPTY_FILTERS);

    // Filter options, built from the tickets currently shown.
    const selectOptions = useMemo(() => {
        const opts = {};
        LIST_COLUMNS.forEach((c) => {
            const map = new Map();
            displayedTasks.forEach((t) => {
                const label = c.text(t);
                if (label && !map.has(label)) map.set(label, c.sort(t));
            });
            opts[c.id] = Array.from(map.entries())
                .sort((a, b) => (a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0))
                .map(([label]) => label);
        });
        return opts;
    }, [displayedTasks]);

    const activeFilterColumn = LIST_COLUMNS.find((c) => c.id === filterPopover.colId);

    const visibleOptions = useMemo(() => {
        if (!filterPopover.colId) return [];
        const q = optionSearch.trim().toLowerCase();
        const all = selectOptions[filterPopover.colId] || [];
        return q ? all.filter((o) => o.toLowerCase().includes(q)) : all;
    }, [filterPopover.colId, selectOptions, optionSearch]);

    const openFilterPopover = (event, colId) => {
        setFilterPopover({ anchorEl: event.currentTarget, colId });
        setDraftSelection(columnFilters[colId] || []);
        setOptionSearch('');
    };

    const closeFilterPopover = () => setFilterPopover({ anchorEl: null, colId: null });

    const toggleDraftOption = (opt) =>
        setDraftSelection((prev) =>
            prev.includes(opt) ? prev.filter((o) => o !== opt) : [...prev, opt]
        );

    const selectAllVisible = () =>
        setDraftSelection((prev) => Array.from(new Set([...prev, ...visibleOptions])));

    const clearDraftSelection = () => setDraftSelection([]);

    const applyFilter = () => {
        const allOptions = selectOptions[filterPopover.colId] || [];
        // Everything selected is the same as no filter, so store it as empty.
        const value = draftSelection.length === allOptions.length ? [] : draftSelection;
        handleColumnFilter(filterPopover.colId, value);
        closeFilterPopover();
    };

    // const listTasks = useMemo(() => {
    //     const filtered = displayedTasks.filter((task) =>
    //         LIST_COLUMNS.every((col) => {
    //             const selected = columnFilters[col.id];
    //             if (!selected || selected.length === 0) return true;
    //             return selected.includes(col.text(task));
    //         })
    //     );

    //     const col = LIST_COLUMNS.find((c) => c.id === sortState.by);
    //     if (!col) return filtered;
    //     const factor = sortState.dir === 'asc' ? 1 : -1;
    //     return [...filtered].sort((a, b) => {
    //         const av = col.sort(a);
    //         const bv = col.sort(b);
    //         if (av < bv) return -1 * factor;
    //         if (av > bv) return 1 * factor;
    //         return 0;
    //     });
    // }, [displayedTasks, columnFilters, sortState]);

    // ---------- Details dialog ----------
    const openTask = (task) => setSelectedGridTask(task);

    const handleCloseModal = () => {
        setSelectedGridTask(null);
        setLinkError('');
    };

    const handleOpenExternal = async (taskID) => {
        if (isOpeningTicket) return;

        if (!userEmail) {
            setLinkError('Could not find your email. Please try again.');
            return;
        }

        setLinkError('');
        setIsOpeningTicket(true);

        // Open the tab synchronously so popup blockers treat it as a user action.
        const newTab = window.open('', '_blank');
        if (newTab) newTab.opener = null;

        try {
            const token = generateToken(10);
            const res = await customAxios.post(LOGIN_TOKEN_ENDPOINT, { loginToken: token, email: userEmail }, { headers: authHeader() });

            if (res.status === 200) {
                const url = `${HELPDESK_LOGIN_URL}?token=${encodeURIComponent(token)}&task_id=${taskID}`;
                console.log("URL ==", url)
                if (newTab) {
                    newTab.location.href = url;
                } else {
                    // Popup was blocked, so fall back to the current tab
                    window.location.href = url;
                }
            } else {
                throw new Error(`Unexpected status ${res.status}`);
            }
        } catch (err) {
            if (newTab) newTab.close();
            setLinkError('Unable to open Help Desk right now. Please try again.');
        } finally {
            setIsOpeningTicket(false);
        }
    };

    return {
        // data
        statuses,
        displayedTasks,
        gridGroups,
        listTasks: tasks,
        displayCounts,
        totalBadgeCount,
        isLoading,
        isSubmitting,
        error,
        refresh: loadAll,

        // search / tabs / view
        searchQuery,
        setSearchQuery,
        isSearching,
        viewMode,
        setViewMode,
        activeTabIndex,
        handleTabChange,

        // list sorting + filters
        sortState,
        handleSort,
        columnFilters,
        hasColumnFilters,
        clearColumnFilters,
        filterPopover,
        activeFilterColumn,
        visibleOptions,
        draftSelection,
        optionSearch,
        setOptionSearch,
        openFilterPopover,
        closeFilterPopover,
        toggleDraftOption,
        selectAllVisible,
        clearDraftSelection,
        applyFilter,

        // create modal
        isModalOpen,
        openCreateModal,
        closeCreateModal,
        handleSubmitTask,

        // details dialog
        selectedGridTask,
        openTask,
        handleCloseModal,
        handleOpenExternal,
        isOpeningTicket,
        linkError,
    };
}